import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { Button, Group, Loader, SimpleGrid, Stack, Title } from '@mantine/core';
import { IconGaugeOff, IconPlus } from '@tabler/icons-react';
import { EmptyState } from '../../../components/EmptyState';
import { SiloReadingsRegister } from '../../../features/silo-counts/components/SiloReadingsRegister';
import { SiloStatusCard } from '../../../features/silo-counts/components/SiloStatusCard';
import { useSiloLocations } from '../../../features/silo-counts/api';

/** Silo status overview (BUILD_SPEC.md §8.2: "A silo status overview (each
 * silo's latest reading / estimated level)"). Moved off the section's index
 * path to its own explicit route — counts-first ruling, 2026-08-24: Howick has
 * no silos, so this can no longer be the default landing page for the Silo &
 * Counts section (silo-counts/index.tsx now redirects to ../counts instead);
 * the tab strip still surfaces this page second.
 *
 * `beforeLoad` guard (Stuart ruling 2026-08-26: silos are a per-tenant module,
 * default OFF) — chosen over a render-time redirect because `context.auth` is
 * already threaded into the router (see router.ts/main.tsx's `AppRouter`,
 * and `_authenticated.tsx`'s own `beforeLoad` guard for the same pattern), so
 * there's no need to render this page's loading state first just to bounce
 * away from it. */
export const Route = createFileRoute('/_authenticated/silo-counts/status')({
  beforeLoad: ({ context }) => {
    if (context.auth.activeTenant?.settings.silosEnabled !== true) {
      throw redirect({ to: '/silo-counts/counts' });
    }
  },
  component: SiloOverviewPage,
});

function SiloOverviewPage() {
  const locationsQuery = useSiloLocations();
  const locations = locationsQuery.data?.items ?? [];

  return (
    <Stack gap="xl">
      <Group justify="space-between">
        <Title order={2}>Silo status</Title>
        <Button component={Link} to="/silo-counts/new-reading" leftSection={<IconPlus size={16} />}>
          New reading
        </Button>
      </Group>

      {locationsQuery.isLoading ? (
        <Loader size="sm" />
      ) : locations.length === 0 ? (
        <EmptyState icon={IconGaugeOff} title="No SILO locations configured yet" />
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
          {locations.map((location) => (
            <SiloStatusCard key={location.id} location={location} />
          ))}
        </SimpleGrid>
      )}

      <Stack gap="sm">
        <Title order={3}>Recent readings</Title>
        <SiloReadingsRegister />
      </Stack>
    </Stack>
  );
}
